import React, { useState, useEffect } from 'react';
import { 
  Facebook, 
  Instagram, 
  Linkedin, 
  Youtube,
  MessageCircle,
  Video,
  Save,
  Loader2,
  ExternalLink,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import * as api from '../services/api';

const PLATFORMS = [
  { 
    id: 'facebook', 
    name: 'Facebook', 
    icon: Facebook, 
    color: '#1877F2',
    idField: 'facebook_page_id',
    nameField: 'facebook_page_name',
    idLabel: 'Page ID',
    nameLabel: 'Nombre de Página',
    urlBase: 'https://facebook.com/'
  },
  { 
    id: 'instagram', 
    name: 'Instagram', 
    icon: Instagram, 
    color: '#E4405F',
    idField: 'instagram_account_id',
    nameField: 'instagram_username',
    idLabel: 'Account ID',
    nameLabel: 'Username',
    urlBase: 'https://instagram.com/'
  },
  { 
    id: 'whatsapp', 
    name: 'WhatsApp', 
    icon: MessageCircle, 
    color: '#25D366',
    idField: 'whatsapp_phone_id',
    nameField: 'whatsapp_phone_number',
    idLabel: 'Phone Number ID',
    nameLabel: 'Número de Teléfono',
    urlBase: null
  },
  { 
    id: 'linkedin', 
    name: 'LinkedIn', 
    icon: Linkedin, 
    color: '#0A66C2',
    idField: 'linkedin_company_id',
    nameField: 'linkedin_company_name',
    idLabel: 'Company ID',
    nameLabel: 'Nombre de Empresa',
    urlBase: 'https://linkedin.com/company/'
  },
  { 
    id: 'tiktok', 
    name: 'TikTok', 
    icon: Video, 
    color: '#000000',
    idField: 'tiktok_account_id',
    nameField: 'tiktok_username',
    idLabel: 'Account ID',
    nameLabel: 'Username',
    urlBase: 'https://tiktok.com/@'
  },
  { 
    id: 'youtube', 
    name: 'YouTube', 
    icon: Youtube, 
    color: '#FF0000',
    idField: 'youtube_channel_id',
    nameField: 'youtube_channel_name',
    idLabel: 'Channel ID',
    nameLabel: 'Nombre del Canal',
    urlBase: 'https://youtube.com/channel/'
  }
];

const SocialMediaTab = ({ empresa, onUpdate }) => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadConfig();
  }, [empresa.id]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await api.default.get(`/empresas/${empresa.id}/social-config`);
      setConfig(response.data || {});
    } catch (error) {
      console.error('Error loading social config:', error);
      setConfig({});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.default.put(`/empresas/${empresa.id}/social-config`, config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving social config:', error);
      alert('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const isConfigured = (platform) => {
    return config[platform.idField] && config[platform.idField].trim() !== '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-[#D4A843]" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800">Redes Sociales</h3>
          <p className="text-sm text-gray-500">Configura las cuentas de redes sociales de este cliente</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className={`${saved ? 'bg-green-600 hover:bg-green-700' : 'bg-[#D4A843] hover:bg-[#c49a3a]'}`}
        >
          {saving ? (
            <Loader2 className="animate-spin mr-2" size={16} />
          ) : saved ? (
            <CheckCircle size={16} className="mr-2" />
          ) : (
            <Save size={16} className="mr-2" />
          )}
          {saved ? 'Guardado' : 'Guardar'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PLATFORMS.map(platform => {
          const configured = isConfigured(platform);
          const PlatformIcon = platform.icon;
          
          return (
            <div 
              key={platform.id}
              className={`border rounded-lg p-4 transition-all ${
                configured ? 'border-green-200 bg-green-50/50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${platform.color}20` }}
                >
                  <PlatformIcon size={20} style={{ color: platform.color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{platform.name}</span>
                    {configured && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        Configurado
                      </span>
                    )}
                  </div>
                  {configured && config[platform.nameField] && platform.urlBase && (
                    <a 
                      href={`${platform.urlBase}${config[platform.nameField]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                    >
                      @{config[platform.nameField]}
                      <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">{platform.idLabel}</Label>
                  <Input
                    value={config[platform.idField] || ''}
                    onChange={e => updateField(platform.idField, e.target.value)}
                    placeholder={`Ingresa el ${platform.idLabel}`}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{platform.nameLabel}</Label>
                  <Input
                    value={config[platform.nameField] || ''}
                    onChange={e => updateField(platform.nameField, e.target.value)}
                    placeholder={`Ingresa ${platform.nameLabel}`}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-blue-500 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">¿Dónde encuentro estos IDs?</p>
            <ul className="space-y-1 text-blue-700">
              <li><strong>Facebook Page ID:</strong> Ve a la página → About → Page ID</li>
              <li><strong>Instagram Account ID:</strong> Meta Business Suite → Settings → Instagram Account</li>
              <li><strong>WhatsApp Phone ID:</strong> Meta for Developers → WhatsApp → Phone Numbers</li>
              <li><strong>LinkedIn Company ID:</strong> URL de la página de empresa</li>
              <li><strong>YouTube Channel ID:</strong> YouTube Studio → Settings → Channel → Advanced</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaTab;
